import { useEffect, useMemo, useState } from "react";
import { FileText, ChevronDown, ChevronRight, ListChecks } from "lucide-react";
import { useQuestionnaireTemplateStore, type QuestionnaireTemplate } from "../../store/useQuestionnaireTemplateStore";

const typeLabel: Record<string, string> = {
  text: "Short text", longtext: "Long text", email: "Email", phone: "Phone",
  multiselect: "Multi-select", singleselect: "Single-select", file: "File upload",
};

export default function TemplatesLibrary() {
  const { templates, loading, fetchTemplates } = useQuestionnaireTemplateStore();
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const byCategory = useMemo(() => {
    const map: Record<string, QuestionnaireTemplate[]> = {};
    templates.forEach((t) => { (map[t.category] = map[t.category] || []).push(t); });
    return map;
  }, [templates]);

  if (loading && !templates.length) return <div className="text-center py-12 text-outline">Loading templates…</div>;
  if (!templates.length) return <div className="text-center py-12 text-outline">No templates available.</div>;

  return (
    <div className="space-y-6">
      <p className="text-[13px] text-on-surface-variant">
        {templates.length} system questionnaire templates — available to every BD. Each starts with the Standard Contact Block and ends with a Future Services section. Pick one when creating a questionnaire.
      </p>
      {Object.entries(byCategory).map(([category, list]) => (
        <div key={category}>
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-tertiary mb-2">{category}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {list.map((t) => {
              const isOpen = open === t._id;
              const sections = [...new Set(t.questions.map((q) => q.section))];
              return (
                <div key={t._id} className="bg-surface-container-lowest border border-outline-variant rounded-xl card-shadow overflow-hidden">
                  <button onClick={() => setOpen(isOpen ? null : t._id)} className="w-full flex items-start gap-3 p-4 text-left hover:bg-surface-container-low/50">
                    <FileText size={18} className="text-primary shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-bold text-on-surface">{t.name}</h4>
                        {isOpen ? <ChevronDown size={16} className="text-outline" /> : <ChevronRight size={16} className="text-outline" />}
                      </div>
                      <p className="text-[12px] text-on-surface-variant mt-0.5">{t.description}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className="text-[10px] font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full inline-flex items-center gap-1"><ListChecks size={10} /> {t.questions.length} questions</span>
                        {sections.map((s) => <span key={s} className="text-[10px] font-semibold bg-surface-container text-on-surface-variant px-2 py-0.5 rounded-full">{s}</span>)}
                      </div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-outline-variant p-4 space-y-3 bg-surface-container-low/30">
                      {sections.map((section) => (
                        <div key={section}>
                          <div className="text-[10px] font-bold uppercase tracking-wide text-outline mb-1.5">{section}</div>
                          <div className="space-y-1.5">
                            {t.questions.filter((q) => q.section === section).map((q, i) => (
                              <div key={i} className="text-[12px]">
                                <div className="flex items-start gap-1.5">
                                  <span className="text-on-surface">{q.question}{q.required && <span className="text-error"> *</span>}</span>
                                  <span className="text-[10px] text-outline whitespace-nowrap">· {typeLabel[q.answerType] || q.answerType}</span>
                                </div>
                                {q.options.length > 0 && (
                                  <div className="text-[11px] text-on-surface-variant pl-3 mt-0.5">{q.options.join(" · ")}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
